import API from "../../services/api";
import Paginator from "../../shared/Paginator";
import { useEffect, useState } from "react";
import { Toast } from "../../shared/Toast";
import config from "../../config";
import { roles } from "../../utils/constants";
import ContentDrawer from "../Designer/components/ContentDrawer";

const PAGE_SIZE = 10;

const STATUS_BADGE = {
  pending: "bg-yellow-100 text-yellow-700 ring-yellow-200",
  success: "bg-green-100 text-green-700 ring-green-200",
  rejected: "bg-red-100 text-red-700 ring-red-200",
};

const STATUS_LABEL = {
  pending: "Pending",
  success: "Accepted",
  rejected: "Rejected",
};

const parsePayment = (raw = "") => {
  try {
    const clean = raw.replace(/_{2,}/g, "").replace(/^"|"$/g, "");
    return JSON.parse(clean);
  } catch {
    return null;
  }
};

/// amounts are INR — group them the Indian way so a 7 figure payout is readable at a glance
const formatINR = (value) => {
  const n = Number(value);
  if (value === null || value === undefined || value === "" || Number.isNaN(n)) return null;
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const Dash = () => <span className="text-gray-300">—</span>;

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ring-1 ring-inset ${
      STATUS_BADGE[status] ?? "bg-gray-100 text-gray-600 ring-gray-200"
    }`}
  >
    {STATUS_LABEL[status] ?? status ?? "unknown"}
  </span>
);

const ActionModal = ({ row, onClose, onDone }) => {
  const [action, setAction] = useState("");
  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tdsDeducted, setTdsDeducted] = useState(false);
  const [tdsAmount, setTdsAmount] = useState("");

  const selectAction = (opt) => {
    setAction(opt);
    if (opt !== "success") {
      setTdsDeducted(false);
      setTdsAmount("");
    }
  };

  const submit = async () => {
    if (!action) return setError("Select an action.");
    if (action === "rejected" && !remark.trim()) return setError("Remark is required for rejection.");
    setError("");
    setLoading(true);
    try {
      const result = await API({
        url: "/order/alphaTransactionActions",
        method: "POST",
        data: {
          statement_id: row.statement_id,
          action,
          ...(remark.trim() ? { remark: remark.trim() } : {}),
          ...(action === "success" && tdsDeducted && String(tdsAmount).trim() ? { tds_amount: tdsAmount } : {}),
          creative_id : row?.creative_id
        },
      });
      if(result.status==='SUCCESS'){
        onDone();
      }else{
        Toast('error',result.data?.message || 'Action failed. Please try again.');
      }
    } catch {
      Toast('error','Action failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isReject = action === "rejected";

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl"
      >
        {/* header */}
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">Review payment request</h3>
            <p className="mt-0.5 truncate font-mono text-xs text-gray-500">Statement #{row.statement_id}</p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
            className="-mr-1 rounded-lg p-1 text-2xl leading-none text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            &times;
          </button>
        </div>

        {/* body */}
        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-3 rounded-xl bg-gray-50 p-4 text-sm sm:grid-cols-2">
            <div className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Order ID</dt>
              <dd className="mt-0.5 truncate font-mono text-xs text-gray-800">{row.order_id || "—"}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Amount</dt>
              <dd className="mt-0.5 font-semibold text-gray-900">{formatINR(row.order_amount) ?? "—"}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Creative</dt>
              <dd className="mt-0.5 truncate text-gray-800">{row.creative_name || "—"}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Mode</dt>
              <dd className="mt-0.5 capitalize text-gray-800">{row.transaction_mode?.replace(/_/g, " ") || "—"}</dd>
            </div>
          </dl>

          {row.transaction_status === "pending" ? (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Decision</p>
              <div className="flex gap-3">
                {["success", "rejected"].map((opt) => (
                  <button
                    key={opt}
                    onClick={() => selectAction(opt)}
                    aria-pressed={action === opt}
                    className={`flex-1 rounded-xl border py-2.5 text-sm font-semibold transition-all
                      ${
                        action === opt
                          ? opt === "success"
                            ? "border-green-600 bg-green-600 text-white shadow-sm"
                            : "border-red-600 bg-red-600 text-white shadow-sm"
                          : opt === "success"
                            ? "border-gray-300 text-gray-600 hover:border-green-300 hover:bg-green-50 hover:text-green-700"
                            : "border-gray-300 text-gray-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                      }`}
                  >
                    {opt === "success" ? "✓ Accept" : "✕ Reject"}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {action === "success" && (
            <div className="rounded-xl border border-gray-200 p-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={tdsDeducted}
                  onChange={(e) => {
                    setTdsDeducted(e.target.checked);
                    if (!e.target.checked) setTdsAmount("");
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                TDS deducted
              </label>
              {tdsDeducted && (
                <div className="mt-3">
                  <label className="mb-1 block text-xs font-medium text-gray-600">TDS amount (₹)</label>
                  <input
                    type="number"
                    value={tdsAmount}
                    onChange={(e) => setTdsAmount(e.target.value)}
                    placeholder="0"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-[11px] text-gray-400">Recorded against the transaction. The received amount stays gross.</p>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Remark {isReject && <span className="text-red-500">*</span>}
            </label>
            <textarea
              rows={3}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder={isReject ? "Why is this being rejected?" : "Add a remark (optional)..."}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>

        {/* footer */}
        <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={loading || !action}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50
              ${isReject ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}
          >
            {loading && (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
            )}
            {loading ? "Submitting..." : isReject ? "Confirm rejection" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AlphaCashFreeLinks = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selected, setSelected] = useState(null);
  const [creativeData, setCreativeData] = useState(null);

  const isFinanceUser = localStorage.getItem('role') === roles.financer;

  const getData = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const result = await API({
        url: "/order/getAlphaPaymentLinks",
        method: "POST",
        responseType: "json",
        /// offset is a PAGE INDEX here — the API multiplies it by limit (LIMIT $1 OFFSET $2 with [limit, limit*offset])
        data: { offset: currentPage, limit: PAGE_SIZE },
      });
      /// the old code reached straight into result.data.total[0] — a failed call threw a TypeError and left the list stale
      const rows = Array.isArray(result?.data?.data) ? result.data.data : [];
      const count = Number(result?.data?.total?.[0]?.count ?? 0);
      const pages = Math.ceil(count / PAGE_SIZE);
      setTotalCount(count);
      setTotalPages(pages);
      setData(rows);
      /// actioning the last row on the last page shrinks the range — step back instead of stranding the user on an empty page
      const lastPage = Math.max(pages - 1, 0);
      if (currentPage > 0 && currentPage > lastPage) setCurrentPage(lastPage);
    } catch (e) {
      setData([]);
      setTotalPages(0);
      setTotalCount(0);
      const message = e?.data?.message || e?.message || "Unable to load payment requests. Please try again.";
      setLoadError(message);
      Toast('error', message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreativeNameClick = async(creative_id) => {
    if(!creative_id) return;
    const result = await API({
         url:'/alpha_cst_designer/getAdDetailsByCreativeId',
         method:'POST',
         data:{creative_id}
    })
    if(result.status==='SUCCESS'){
        setCreativeData(result.data[0]);
    }
  }
  const handlePageClick = (event) => setCurrentPage(event.selected);

  useEffect(() => {
    getData();
  }, [currentPage]);

  const renderAction = (r) =>
    r.transaction_status === "pending" && isFinanceUser ? (
      <button
        onClick={() => setSelected(r)}
        className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 transition hover:bg-blue-100"
      >
        Review
      </button>
    ) : (
      <span className="text-xs text-gray-400">—</span>
    );

  const renderProof = (r) => {
    const p = parsePayment(r.mode_of_payment);
    return p?.proof ? (
      <a href={p.proof} target="_blank" rel="noreferrer" className="text-xs font-medium text-blue-600 hover:underline">
        View
      </a>
    ) : (
      <span className="text-xs">
        <Dash />
      </span>
    );
  };

  const renderTxnId = (r) => {
    const p = parsePayment(r.mode_of_payment);
    return p?.transaction_id ? (
      <span className="font-mono text-xs text-gray-600">{p.transaction_id}</span>
    ) : (
      <Dash />
    );
  };

  const cols = [
    {
      label: "#",
      render: (_, i) => <span className="text-xs text-gray-400">{currentPage * PAGE_SIZE + i + 1}</span>,
      align: "text-right",
      width: "w-12",
    },
    {
      label: "Statement ID",
      render: (r) => <span className="font-mono text-xs text-gray-800">{r.statement_id}</span>,
      align: "text-right",
    },
    {
      label: "Creative",
      render: (r) =>
        r.creative_name ? (
          <button
            type="button"
            onClick={() => handleCreativeNameClick(r.creative_id)}
            title={r.creative_name}
            className="block max-w-[180px] truncate text-left text-sm font-medium text-blue-600 hover:underline"
          >
            {r.creative_name}
          </button>
        ) : (
          <Dash />
        ),
    },
    {
      label: "Order ID",
      render: (r) =>
        r.order_id ? (
          <span title={r.order_id} className="block max-w-[180px] truncate font-mono text-xs text-gray-500">
            {r.order_id}
          </span>
        ) : (
          <Dash />
        ),
    },
    {
      label: "Amount",
      render: (r) => <span className="font-semibold tabular-nums text-gray-900">{formatINR(r.order_amount) ?? <Dash />}</span>,
      align: "text-right",
    },
    {
      label: "TDS",
      render: (r) => {
        const tds = formatINR(r.tds_amount);
        return tds && Number(r.tds_amount) > 0 ? (
          <span className="tabular-nums text-xs text-gray-700">{tds}</span>
        ) : (
          <Dash />
        );
      },
      align: "text-right",
    },
    {
      label: "Mode",
      render: (r) =>
        r.transaction_mode ? <span className="text-xs capitalize text-gray-700">{r.transaction_mode.replace(/_/g, " ")}</span> : <Dash />,
    },
    { label: "Status", render: (r) => <StatusBadge status={r.transaction_status} /> },
    { label: "Proof", render: renderProof, align: "text-center" },
    { label: "Txn ID", render: renderTxnId },
    {
      label: "Remarks",
      render: (r) =>
        r.remarks ? (
          <span title={r.remarks} className="block max-w-[200px] truncate text-xs text-gray-500">
            {r.remarks}
          </span>
        ) : (
          <Dash />
        ),
    },
    { label: "Action", render: renderAction, align: "text-right" },
  ];

  const skeletonRows = Array.from({ length: 5 });

  return (
    <div className="w-full max-w-full overflow-x-hidden p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Alpha Payment Approvals</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {loading
              ? "Loading payment requests..."
              : loadError
                ? "Could not load payment requests."
                : `${totalCount} request${totalCount === 1 ? "" : "s"}${totalPages > 1 ? ` · page ${currentPage + 1} of ${totalPages}` : ""}`}
          </p>
        </div>
        {!isFinanceUser && !loading && !loadError && (
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">View only</span>
        )}
      </div>

      {loadError && !loading && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{loadError}</p>
          <button
            onClick={getData}
            className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {/* ---------- md+ : full table, sticky header, scrolls inside its own box ----------
          Uses `max-md:hidden`, NOT `hidden md:block`: Styles.scss defines a global
          `.hidden{display:none!important}` that `.md\:block` can never override. */}
      <div className="max-md:hidden overflow-hidden rounded-xl border border-gray-200 shadow-sm">
        <div className="max-h-[70vh] overflow-auto">
          <table className="min-w-full text-sm text-gray-700">
            <thead className="sticky top-0 z-10 bg-gray-50 text-xs uppercase tracking-wide text-gray-500 shadow-[inset_0_-1px_0_0_rgb(229,231,235)]">
              <tr>
                {cols.map((c) => (
                  <th
                    key={c.label}
                    className={`whitespace-nowrap px-4 py-3 font-medium ${c.align ?? "text-left"} ${c.width ?? ""}`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                skeletonRows.map((_, i) => (
                  <tr key={`sk-${i}`}>
                    {cols.map((c) => (
                      <td key={c.label} className="px-4 py-3.5">
                        <div className="h-3 animate-pulse rounded bg-gray-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={cols.length} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-500">
                      {loadError ? "Couldn't load records." : "No payment requests found."}
                    </p>
                    {!loadError && <p className="mt-1 text-xs text-gray-400">New Alpha payment requests will show up here.</p>}
                  </td>
                </tr>
              ) : (
                data.map((row, i) => (
                  <tr key={row.statement_id} className="transition-colors hover:bg-gray-50">
                    {cols.map((c) => (
                      <td key={c.label} className={`whitespace-nowrap px-4 py-3 align-middle ${c.align ?? "text-left"}`}>
                        {c.render(row, i)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------- below md : one card per record ---------- */}
      <div className="space-y-3 md:hidden">
        {loading ? (
          skeletonRows.map((_, i) => (
            <div key={`skc-${i}`} className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="h-3 w-1/3 animate-pulse rounded bg-gray-100" />
              <div className="h-4 w-2/3 animate-pulse rounded bg-gray-100" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
            </div>
          ))
        ) : data.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 px-4 py-10 text-center">
            <p className="text-sm font-medium text-gray-500">
              {loadError ? "Couldn't load records." : "No payment requests found."}
            </p>
            {!loadError && <p className="mt-1 text-xs text-gray-400">New Alpha payment requests will show up here.</p>}
          </div>
        ) : (
          data.map((row) => {
            const payment = parsePayment(row.mode_of_payment);
            const tds = formatINR(row.tds_amount);
            return (
              <div key={row.statement_id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono text-[11px] uppercase tracking-wide text-gray-400">#{row.statement_id}</p>
                    {row.creative_name ? (
                      <button
                        type="button"
                        onClick={() => handleCreativeNameClick(row.creative_id)}
                        className="mt-0.5 block max-w-full truncate text-left text-sm font-semibold text-blue-600"
                      >
                        {row.creative_name}
                      </button>
                    ) : (
                      <p className="mt-0.5 text-sm font-semibold text-gray-400">Unnamed creative</p>
                    )}
                  </div>
                  <StatusBadge status={row.transaction_status} />
                </div>

                <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-lg font-semibold tabular-nums text-gray-900">{formatINR(row.order_amount) ?? "—"}</span>
                  {row.transaction_mode && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium capitalize text-gray-600">
                      {row.transaction_mode.replace(/_/g, " ")}
                    </span>
                  )}
                  {tds && Number(row.tds_amount) > 0 && (
                    <span className="text-[11px] text-gray-500">TDS {tds}</span>
                  )}
                </div>

                <dl className="mt-3 space-y-1.5 border-t border-gray-100 pt-3 text-xs text-gray-500">
                  <div className="flex justify-between gap-3">
                    <dt>Order ID</dt>
                    <dd className="truncate font-mono text-gray-700">{row.order_id || "—"}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt>Txn ID</dt>
                    <dd className="truncate font-mono text-gray-700">{payment?.transaction_id ?? "—"}</dd>
                  </div>
                  {row.remarks && (
                    <div className="flex justify-between gap-3">
                      <dt>Remarks</dt>
                      <dd className="max-w-[60%] break-words text-right text-gray-700">{row.remarks}</dd>
                    </div>
                  )}
                </dl>

                <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
                  {payment?.proof ? (
                    <a
                      href={payment.proof}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-medium text-blue-600 hover:underline"
                    >
                      View proof
                    </a>
                  ) : (
                    <span className="text-xs text-gray-400">No proof</span>
                  )}
                  {renderAction(row)}
                </div>
              </div>
            );
          })
        )}
      </div>

      <Paginator pageCount={totalPages} handlePageClick={handlePageClick} forcePage={currentPage} />
     {creativeData && <ContentDrawer isOpen={!!creativeData} rowData={creativeData}  type='adDetails' onClose={() => setCreativeData(null)} />}
      {selected && (
        <ActionModal
          row={selected}
          onClose={() => setSelected(null)}
          onDone={() => {
            setSelected(null);
            getData();
          }}
        />
      )}
    </div>
  );
};

export default AlphaCashFreeLinks;
